import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import moment from 'moment';
import {
  DEFAULT_ORDER_SERVICE_TYPE,
  normalizeOrderServiceType,
} from '@constants/orderServiceType';
import type {
  KotDailySummary,
  KotLogEntry,
  KotLogItem,
  KotLogPrintedBy,
  KotLogWriteInput,
} from '@src/types/kotLogging';
import { isKotSessionCartKey } from '@utilities/kotSession';

export const KOT_LOGGING_COLLECTION = 'kot-logging';

const createClientRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `kot-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const readPrintedByFromStorage = (): KotLogPrintedBy => {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) {
      return { uid: '', phone: '', name: '' };
    }
    const user = JSON.parse(raw) as Record<string, unknown>;
    return {
      uid: String(user.uid || ''),
      phone: String(user.phoneNumber || user.phone || ''),
      name: String(user.name || ''),
    };
  } catch {
    return { uid: '', phone: '', name: '' };
  }
};

const mapDocToKotLogEntry = (id: string, data: Record<string, any>): KotLogEntry => {
  const items = normalizeKotLogItems(Array.isArray(data.items) ? data.items : []);
  // Prefer recomputed sum so older logs (no unitPrice / totalItemsCost: 0) backfill correctly
  const fromItems = Number(sumKotItemsCost(items).toFixed(2));
  const stored = Number(data.totalItemsCost);
  const totalItemsCost =
    Number.isFinite(stored) && stored > 0 ? stored : fromItems;

  return {
    id,
    restaurantId: String(data.restaurantId || ''),
    tableNumber: String(data.tableNumber || ''),
    serviceType: normalizeOrderServiceType(data.serviceType),
    printType: data.printType,
    source: data.source,
    uoId: data.uoId ?? null,
    orderId: data.orderId ?? null,
    kotSessionId: data.kotSessionId ?? null,
    items,
    itemCount: Number(data.itemCount) || items.length,
    totalQty: Number(data.totalQty) || items.reduce((s, i) => s + (Number(i.qty) || 0), 0),
    totalItemsCost,
    printedBy: data.printedBy || { uid: '', phone: '', name: '' },
    printerChannel: data.printerChannel,
    status: 'printed',
    localDate: String(data.localDate || ''),
    printedAtMs: Number(data.printedAtMs) || 0,
    clientRequestId: String(data.clientRequestId || id),
    createdAt: data.createdAt,
    printedAt: data.printedAt,
  };
};

/** Coerce menu/cart price fields that may be number, numeric string, or "₹150 / Full". */
export function coerceMoney(raw: unknown): number {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : 0;
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if ('value' in obj) return coerceMoney(obj.value);
    if ('cost' in obj) return coerceMoney(obj.cost);
    if ('unitPrice' in obj) return coerceMoney(obj.unitPrice);
    return 0;
  }

  const str = String(raw).replace(/,/g, '').trim();
  if (!str) return 0;

  const direct = Number(str);
  if (Number.isFinite(direct) && str !== '') return direct;

  // Labels like "₹150 / Full", "150/-", "Rs 40 / cup"
  const match = str.match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

/** Cart keys are `category-itemName-{price}` — last numeric segment is unit price. */
export function unitPriceFromCartKey(key: string): number {
  const match = String(key || '').match(/-(\d+(?:\.\d+)?)$/);
  return match ? Number(match[1]) : 0;
}

/**
 * Resolve unit price from a live cart line.
 * Prefer selectedVariants.value (same as billing); fall back to label / cart key.
 */
export function resolveCartItemUnitPrice(
  key: string,
  val: {
    selectedVariants?: { label?: string; value?: unknown } | Array<{ label?: string; value?: unknown }>;
    unitPrice?: unknown;
    price?: unknown;
    cost?: unknown;
  } | null | undefined
): number {
  if (!val || typeof val !== 'object') {
    return unitPriceFromCartKey(key);
  }

  const sv = Array.isArray(val.selectedVariants)
    ? val.selectedVariants[0]
    : val.selectedVariants;

  // Match Cart/billing: value may be a string cost from Inventory TextFields
  const fromValue = coerceMoney(sv?.value);
  if (fromValue > 0) return fromValue;

  const fromLabel = coerceMoney(sv?.label);
  if (fromLabel > 0) return fromLabel;

  const fromKey = unitPriceFromCartKey(key);
  if (fromKey > 0) return fromKey;

  return (
    coerceMoney(val.unitPrice) ||
    coerceMoney(val.price) ||
    coerceMoney(val.cost) ||
    0
  );
}

export function sumKotItemsCost(items: KotLogItem[]): number {
  return items.reduce((sum, item) => {
    const line =
      typeof item.lineTotal === 'number' && Number.isFinite(item.lineTotal)
        ? item.lineTotal
        : (Number(item.unitPrice) || 0) * (Number(item.qty) || 0);
    return sum + line;
  }, 0);
}

/** Backfill price fields for older logs that only stored qty/name/variant. */
export function normalizeKotLogItems(items: any[]): KotLogItem[] {
  return (items || []).map((item) => {
    const key = String(item?.key || '');
    const qty = Number(item?.qty) || 0;
    const variant = String(item?.variant || '');

    let unitPrice = coerceMoney(item?.unitPrice);
    if (unitPrice <= 0) {
      // Older logs: price often still present in variant label ("150 / Full") or cart key
      unitPrice =
        coerceMoney(variant) ||
        unitPriceFromCartKey(key) ||
        coerceMoney(item?.selectedVariants?.value) ||
        coerceMoney(item?.selectedVariants?.label) ||
        0;
    }

    const storedLine = coerceMoney(item?.lineTotal);
    const lineTotal =
      storedLine > 0 ? storedLine : Number((unitPrice * qty).toFixed(2));

    return {
      key,
      name: String(item?.name || ''),
      variant,
      qty,
      unitPrice,
      lineTotal,
      note: String(item?.note || ''),
      category: item?.category ? String(item.category) : undefined,
    };
  });
}

/** Build line items for a KOT log from a cart-like map (qty already final). */
export function buildKotLogItemsFromCart(
  items: Record<
    string,
    {
      name?: string;
      selected?: number;
      category?: string;
      selectedVariants?: { label?: string; value?: unknown };
      itemDescription?: string;
      unitPrice?: unknown;
      price?: unknown;
      cost?: unknown;
    } | null | undefined
  >
): KotLogItem[] {
  const lines: KotLogItem[] = [];

  Object.entries(items || {}).forEach(([key, val]) => {
    if (isKotSessionCartKey(key)) return;
    if (!val || typeof val !== 'object') return;
    const qty = Number(val.selected) || 0;
    if (qty <= 0) return;

    const unitPrice = resolveCartItemUnitPrice(key, val);
    lines.push({
      key,
      name: String(val.name || key).replaceAll('-', ' '),
      variant: String(val.selectedVariants?.label || '').replaceAll('₹', '').trim(),
      qty,
      unitPrice,
      lineTotal: Number((unitPrice * qty).toFixed(2)),
      note: String(val.itemDescription || '').trim(),
      category: val.category ? String(val.category) : undefined,
    });
  });

  return lines;
}

export function buildKotDailySummary(entries: KotLogEntry[]): KotDailySummary {
  const summary: KotDailySummary = {
    kotCount: entries.length,
    totalQty: 0,
    totalItemsCost: 0,
    itemLineCount: 0,
    byPrintType: {},
    byTable: {},
    byUser: {},
    byChannel: {},
  };

  entries.forEach((entry) => {
    summary.totalQty += entry.totalQty || 0;
    summary.totalItemsCost += Number(entry.totalItemsCost) || sumKotItemsCost(entry.items || []);
    summary.itemLineCount += entry.itemCount || 0;

    const printType = entry.printType || 'unknown';
    summary.byPrintType[printType] = (summary.byPrintType[printType] || 0) + 1;

    const table = entry.tableNumber || '—';
    summary.byTable[table] = (summary.byTable[table] || 0) + 1;

    const userLabel = entry.printedBy?.name || entry.printedBy?.phone || entry.printedBy?.uid || 'Unknown';
    summary.byUser[userLabel] = (summary.byUser[userLabel] || 0) + 1;

    const channel = entry.printerChannel || 'unknown';
    summary.byChannel[channel] = (summary.byChannel[channel] || 0) + 1;
  });

  summary.totalItemsCost = Number(summary.totalItemsCost.toFixed(2));
  return summary;
}

/**
 * Fire-and-forget append of a confirmed KOT print.
 * Never throws to callers — logging must not affect POS print/cart flow.
 * Uses clientRequestId as doc id for idempotency (safe retries / double-clicks).
 */
export async function logKotPrint(input: KotLogWriteInput): Promise<string | null> {
  try {
    if (!input?.restaurantId) {
      console.warn('[kot-logging] skipped: missing restaurantId');
      return null;
    }

    const items = (input.items || []).filter((item) => item && item.qty > 0);
    if (items.length === 0) {
      console.warn('[kot-logging] skipped: no items');
      return null;
    }

    const normalizedItems = normalizeKotLogItems(items);
    const totalQty = normalizedItems.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
    const totalItemsCost = Number(sumKotItemsCost(normalizedItems).toFixed(2));

    const clientRequestId = input.clientRequestId || createClientRequestId();
    const printedAtMs = input.printedAtMs || Date.now();
    const localDate = input.localDate || moment(printedAtMs).local().format('YYYY-MM-DD');
    const printedBy: KotLogPrintedBy = {
      ...readPrintedByFromStorage(),
      ...(input.printedBy || {}),
    };

    const payload = {
      restaurantId: input.restaurantId,
      tableNumber: String(input.tableNumber || ''),
      serviceType: normalizeOrderServiceType(input.serviceType || DEFAULT_ORDER_SERVICE_TYPE),
      printType: input.printType,
      source: input.source || 'unknown',
      uoId: input.uoId || null,
      orderId: input.orderId || null,
      kotSessionId: input.kotSessionId || null,
      items: normalizedItems,
      itemCount: normalizedItems.length,
      totalQty,
      totalItemsCost,
      printedBy,
      printerChannel: input.printerChannel,
      status: 'printed' as const,
      localDate,
      printedAtMs,
      clientRequestId,
      printedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    const db = getFirestore();
    const ref = doc(db, KOT_LOGGING_COLLECTION, clientRequestId);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      return existing.id;
    }

    await setDoc(ref, payload);
    return clientRequestId;
  } catch (error) {
    console.error('[kot-logging] failed to write log (print flow unaffected):', error);
    return null;
  }
}

/** Schedule log write without blocking the print/cart path. */
export function logKotPrintAsync(input: KotLogWriteInput): void {
  void logKotPrint(input);
}

export async function getKotLogsForLocalDate(
  restaurantId: string,
  localDate: string
): Promise<KotLogEntry[]> {
  if (!restaurantId || !localDate) {
    return [];
  }

  const db = getFirestore();
  const q = query(
    collection(db, KOT_LOGGING_COLLECTION),
    where('restaurantId', '==', restaurantId),
    where('localDate', '==', localDate),
    where('status', '==', 'printed'),
    orderBy('printedAtMs', 'desc')
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => mapDocToKotLogEntry(d.id, d.data()));
}

/** Inclusive local-day range query via printedAtMs (same pattern as orders report). */
export async function getKotLogsForDateRange(
  restaurantId: string,
  startMs: number,
  endMs: number
): Promise<KotLogEntry[]> {
  if (!restaurantId || !startMs || !endMs) {
    return [];
  }

  const db = getFirestore();
  const q = query(
    collection(db, KOT_LOGGING_COLLECTION),
    where('restaurantId', '==', restaurantId),
    where('status', '==', 'printed'),
    where('printedAtMs', '>=', startMs),
    where('printedAtMs', '<=', endMs),
    orderBy('printedAtMs', 'desc')
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => mapDocToKotLogEntry(d.id, d.data()));
}

/** Sort: newest prints first, then table name A→Z. */
export function sortKotLogsByTableThenDate(entries: KotLogEntry[]): KotLogEntry[] {
  return [...entries].sort((a, b) => {
    const dateCmp = (b.printedAtMs || 0) - (a.printedAtMs || 0);
    if (dateCmp !== 0) return dateCmp;
    return String(a.tableNumber || '').localeCompare(
      String(b.tableNumber || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    );
  });
}
